/**
 * Structured data for the homepage, pricing page and database blog posts.
 * Shared by the build-time tm-static-seo plugin (pre-JS HTML) and the React
 * pages (Helmet), so both emit identical blocks. Values mirror live copy only:
 * Landing.tsx description, LandingPricingTiers plans/prices and the
 * "30-day free trial on all plans" line. No ratings, reviews or awards.
 * Keep dependency-free (imported by vite.config.ts).
 */
const ORIGIN = "https://www.transportmate.co.uk";
const PUBLISHER = { "@type": "Organization", name: "TransportMate Ltd", url: `${ORIGIN}/` } as const;

/** Must match LandingPricingTiers / SUBSCRIPTION_TIERS (asserted in tests). */
export const PUBLIC_PLANS = [
  { name: "Starter", price: 49, jobs: "Up to 75 jobs per month" },
  { name: "Growth", price: 99, jobs: "Up to 200 jobs per month" },
  { name: "Pro", price: 149, jobs: "Unlimited jobs per month" },
] as const;

export const TRIAL_DAYS = 30;

const BASE = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "TransportMate",
  applicationCategory: "BusinessApplication",
  operatingSystem: "iOS, Android, Web",
  url: `${ORIGIN}/`,
  description:
    "Vehicle transport management software for UK operators: jobs, drivers, GPS tracking, digital appraisals with damage photos, proof of collection and delivery, and invoicing with accounting integrations.",
  publisher: PUBLISHER,
};

function planOffer(p: (typeof PUBLIC_PLANS)[number]) {
  return {
    "@type": "Offer",
    name: `${p.name} plan`,
    price: String(p.price),
    priceCurrency: "GBP",
    url: `${ORIGIN}/pricing`,
    description: `${p.jobs}. ${TRIAL_DAYS}-day free trial.`,
    priceSpecification: {
      "@type": "UnitPriceSpecification",
      price: String(p.price),
      priceCurrency: "GBP",
      unitText: "MONTH",
      billingDuration: "P1M",
    },
  };
}

export function buildHomeSoftwareJsonLd(): Record<string, unknown> {
  return {
    ...BASE,
    offers: {
      "@type": "AggregateOffer",
      priceCurrency: "GBP",
      lowPrice: String(PUBLIC_PLANS[0].price),
      highPrice: String(PUBLIC_PLANS[PUBLIC_PLANS.length - 1].price),
      offerCount: PUBLIC_PLANS.length,
      url: `${ORIGIN}/pricing`,
    },
  };
}

export function buildPricingSoftwareJsonLd(): Record<string, unknown> {
  return { ...BASE, url: `${ORIGIN}/pricing`, offers: PUBLIC_PLANS.map(planOffer) };
}

/** Published dates from blg_articles.article_created_at/updated_at (snapshot 2026-10-20). */
export const BLOG_PUBLISHED_DATES: Record<string, { published: string; modified: string }> = {
  "accounting-integration-transport": { published: "2026-10-10", modified: "2026-10-12" },
  "ai-vehicle-damage-detection": { published: "2026-09-24", modified: "2026-09-25" },
  "audit-trail-logistics": { published: "2026-10-07", modified: "2026-10-09" },
  "car-transporter-routing": { published: "2026-10-05", modified: "2026-10-07" },
  "customer-eta-communication": { published: "2026-10-11", modified: "2026-10-13" },
  "customer-self-service-tracking": { published: "2026-10-01", modified: "2026-10-01" },
  "delivery-photo-evidence": { published: "2026-10-14", modified: "2026-10-16" },
  "digital-delivery-notes": { published: "2026-09-29", modified: "2026-09-30" },
  "digital-proof-of-delivery": { published: "2026-09-19", modified: "2026-09-19" },
  "digital-signature-pod": { published: "2026-09-22", modified: "2026-09-22" },
  "dispatch-board-setup": { published: "2026-10-12", modified: "2026-10-14" },
  "dispatch-email-automation": { published: "2026-10-19", modified: "2026-10-19" },
  "driver-app-features": { published: "2026-10-04", modified: "2026-10-06" },
  "driver-hours-rules-uk": { published: "2026-09-22", modified: "2026-09-24" },
  "driver-onboarding-logistics": { published: "2026-10-03", modified: "2026-10-05" },
  "epod-for-vehicle-delivery": { published: "2026-09-26", modified: "2026-09-27" },
  "gps-timestamp-accuracy": { published: "2026-10-01", modified: "2026-10-03" },
  "job-notes-attachments-logistics": { published: "2026-10-02", modified: "2026-10-04" },
  "live-tracking-links": { published: "2026-09-24", modified: "2026-09-26" },
  "pod-requirements-uk": { published: "2026-10-18", modified: "2026-10-18" },
  "pod-retention-policy-uk": { published: "2026-10-15", modified: "2026-10-17" },
  "pod-training-drivers": { published: "2026-10-13", modified: "2026-10-15" },
  "proof-of-collection": { published: "2026-09-21", modified: "2026-09-23" },
  "proof-of-delivery-legal-uk": { published: "2026-10-09", modified: "2026-10-11" },
  "real-time-driver-tracking": { published: "2026-09-29", modified: "2026-09-29" },
  "reducing-delivery-disputes": { published: "2026-10-06", modified: "2026-10-08" },
  "tachograph-rules-uk": { published: "2026-09-21", modified: "2026-09-21" },
  "transport-job-templates": { published: "2026-10-08", modified: "2026-10-10" },
  "transport-kpis-to-track": { published: "2026-09-26", modified: "2026-09-28" },
  "vehicle-condition-report": { published: "2026-09-20", modified: "2026-09-20" },
  "vehicle-handover-checklist": { published: "2026-09-30", modified: "2026-10-02" },
  "vehicle-transporter-regulations-uk": { published: "2026-10-20", modified: "2026-10-20" },
};

const isoDate = (s: string | null | undefined) => (s && /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : undefined);

/** BlogPosting from genuine article fields only; dates omitted when unknown. */
export function buildBlogPostingJsonLd(a: {
  slug: string;
  headline: string;
  description?: string | null;
  published?: string | null;
  modified?: string | null;
}): Record<string, unknown> {
  const url = `${ORIGIN}/blog/${a.slug}`;
  const published = isoDate(a.published);
  const modified = isoDate(a.modified) ?? published;
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: a.headline,
    ...(a.description ? { description: a.description } : {}),
    ...(published ? { datePublished: published } : {}),
    ...(modified ? { dateModified: modified } : {}),
    url,
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    publisher: PUBLISHER,
  };
}
